import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import {
  Loader,
  ErrorFallback,
  TextInput,
  isValidate,
  isFromValid,
} from 'smart-react';
import { Card, CardHeader, CardBody } from '@progress/kendo-react-layout';
import { Error } from '@progress/kendo-react-labels';
import { Link } from 'react-router-dom';
import { Button } from '@progress/kendo-react-buttons';
import { buildNotification, GenerateNotification } from 'smart-react';
import {
  createCommand,
  updateCommand,
  listCommands,
} from '../Services/CommandService';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Form, Field } from '@progress/kendo-react-form';
import {
  EVENTS_DATA_TYPES,
  NOTIFICATION_TYPES,
} from '../../../../constants/eventDataTypes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../Context/AuthContext';
import CommandEditor from '../../Components/CommandEditor/CommandEditor';
import { SplitButton, SplitButtonItem } from '@progress/kendo-react-buttons';
import { base64ToObject } from '../../../../Utils/Common/commonUtils';
import useQueryParams from '../../../Core/CustomHooks/useQueryParams';
import TenantDropdown from '../../Components/TenantDropdown/TenantDropdown';
import NoRecordFound from '../../Components/NoRecordFound/NoRecordFound';
import {
  ValidatePermissions,
  findTenant,
  findTenantById,
} from '../../../../Utils/Auth/ValidatePermissions';
import {
  CREATE_MESSAGE,
  UPDATE_MESSAGE,
  ERROR_MESSAGE,
} from '../../../../constants/notificationMessages';
/**
 * Add New Test main screen
 * @type {React.FC<Props>}
 * @returns {React.ReactElement} The Add New Test component.
 */
const AddCommand = () => {
  const navigate = useNavigate();

  const { tenantId } = useParams();
  const { getParam } = useQueryParams();
  const refId = getParam('refId');
  const { tenantID, getTokensFromStorage } = useAuth(); // Get tenantID using Auth Context
  const [detailsDataModified, setDetailsDataModified] = React.useState(false);
  const [tenant, setTenant] = React.useState(findTenantById(tenantID));
  const [loading, setLoading] = React.useState(false);
  const [isEditor, setIsEditor] = React.useState(false);
  const [validateForm, setValidateForm] = React.useState(false);
  const [noRecord, setNoRecord] = React.useState(false);
  const [formData, setFormData] = React.useState({
    tenant_id: tenantID,
    TenantName: tenant?.TenantName,
    les_cmd_id: '',
    cust_lvl: '-1',
    syntax: '',
    grp_nam: '',
  });
  const requiredFields = ['les_cmd_id', 'syntax', 'TenantName'];
  const [editorField, setEditorField] = React.useState({
    name: '',
    value: '',
    label: '',
  });
  const { state } = useLocation();
  const [testState, setTestState] = React.useState(state);
  const handleSaveSplit = (e) => {
    const { itemIndex } = e;
    switch (itemIndex) {
      case 0: {
        onSave({ isExit: true });
        break;
      }
      case 1:
        onSave({ isContinue: true });
      default: {
      }
    }
  };
  /**
   * Get Command Detail from API for edit case
   * @param {*} stateData
   */
  const fetchCommandData = async (stateData) => {
    const { accessToken } = getTokensFromStorage();

    setLoading(true);

    try {
      let refObj = base64ToObject(refId);
      const response = await listCommands({
        accessToken: accessToken,
        limit: '50',
        offset: 1,
        filter: [
          {
            ColumnName: 'les_cmd_id',
            Op: 'eq',
            ColumnValue: refObj?.les_cmd_id,
          },
          {
            ColumnName: 'tenant_id',
            Op: 'eq',
            ColumnValue: tenantId,
          },
        ],
        isServiceWorker: false,
      });

      if (response?.loadCommands?.length > 0) {
        const testData = response.loadCommands[0];
        setTestState({ ...stateData, dataSet: testData, edit: true });
        setFormData(testData);
        setTenant(findTenant(testData));
        setNoRecord(false);
        if (ValidatePermissions(testData)) {
          setDetailsDataModified(true);
        } else {
          setDetailsDataModified(false);
        }
      } else {
        setNoRecord(true);
      }
    } catch (error) {
      console.error('Error fetching test data:', error);
      // Handle error state if needed
      setNoRecord(true);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (testState || (tenantId && tenantId !== 'new')) {
      const dataSet = state ? state.dataSet : null;
      // Call the async function
      fetchCommandData(state);
      // Rest of the synchronous logic
      if (dataSet) {
        setFormData({ ...dataSet });
        setTenant(findTenant(dataSet));
        if (ValidatePermissions(dataSet)) {
          setDetailsDataModified(true);
        }
      }
    } else {
      setDetailsDataModified(true);
    }
  }, [state]);

  /**
   * on update the tenantID update the formData
   */
  React.useEffect(() => {
    if (!testState?.edit && tenantId === 'new') {
      let tempTenant = findTenantById(tenantID);
      setTenant(tempTenant);
      setFormData((prevState) => ({
        ...prevState,
        tenant_id: tenantID,
        TenantName: tempTenant?.TenantName ? tempTenant?.TenantName : '',
      }));
    }
  }, [tenantID]);

  /**
   * handle Input Change
   * @e object
   */
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? (checked ? 1 : 0) : value;
    setFormData((prevState) => ({
      ...prevState,
      [name]: newValue,
    }));
  };

  /**
   * onSave
   * submit the test data to api
   */
  const onSave = async ({ isExit = false }) => {
    setValidateForm(true);
    if (!isFromValid({ formData, requiredFields })) {
      return;
    }
    setLoading(true);
    let notificationMetaData = {};
    const data = {
      ...formData,
      les_cmd_id: testState?.edit
        ? formData.les_cmd_id
        : tenant?.OrganizationPrefix + formData.les_cmd_id,
      uc_ossi_disable_flg: formData?.uc_ossi_disable_flg ? 1 : 0,
    };
    let response;
    let method;
    if (testState?.edit) {
      method = updateCommand;
      notificationMetaData = UPDATE_MESSAGE;
    } else {
      method = createCommand;
      notificationMetaData = CREATE_MESSAGE;
      setFormData({
        tenant_id: tenantID,
        TenantName: tenant?.TenantName,
        les_cmd_id: '',
        cust_lvl: '-1',
        syntax: '',
        grp_nam: '',
      });
    }

    try {
      setValidateForm(false);
      response = await method({
        data,
        moduleName: 'lcs',
        isServiceWorker: false,
      });

      if (!response.IsSuccess) {
        GenerateNotification(
          buildNotification({
            title: response?.Title,
            description: response.Message,
            style: response?.Style,
          }),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
        );
      } else {
        GenerateNotification(
          buildNotification(notificationMetaData),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
        );
        if (isExit) {
          navigate('/autest/command');
        }
      }
    } catch (error) {
      GenerateNotification(
        buildNotification(ERROR_MESSAGE),
        NOTIFICATION_TYPES.APP,
        EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * onExit
   * back to tests listing screen
   */
  const onExit = () => {
    navigate('/autest/commands');
  };
  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        // reset the state of your app so the error doesn't happen again
      }}
    >
      <div className='container mx-auto'>
        <React.Suspense fallback={<Loader />}>
          {loading && <Loader />}
          {noRecord ? (
            <NoRecordFound
              recordNotFoundText={
                'No Command found against your selected tenant'
              }
              onExit={onExit}
            />
          ) : (
            <>
              <div className={`${isEditor ? '' : 'k-d-none'}`}>
                <CommandEditor
                  label={'Syntax'}
                  renderTypes={!detailsDataModified ? 'ReadOnly' : ''}
                  validateForm={validateForm}
                  formData={formData}
                  setFormData={setFormData}
                  detailsDataModified={detailsDataModified}
                  isEditor={isEditor}
                  setIsEditor={setIsEditor}
                  editorField={editorField}
                  setEditorField={setEditorField}
                  smHeight={'100px'}
                  lgHeight={'70vh'}
                />
              </div>
              <div
                className={`add-test main-page-section add-page-section ${!isEditor ? '' : 'k-d-none'}`}
              >
                <Card className='tophead'>
                  <CardHeader>
                    <div className='action-buttons-container'>
                      <Link
                        className='action-icon-wrapper'
                        to={`/autest/commands`}
                      >
                        <FontAwesomeIcon
                          className='k-mr-2'
                          icon={faArrowLeft}
                        />
                      </Link>
                      <p className='k-align-items-center k-d-flex k-mb-0 display-text'>
                        Command:{' '}
                        {testState?.edit
                          ? (formData?.les_cmd_id ?? '')
                          : `${tenant?.OrganizationPrefix ?? ''}${formData?.les_cmd_id ?? ''}`}
                      </p>
                    </div>
                  </CardHeader>
                </Card>
                <Card className='k-mt-3 detail-card'>
                  <CardHeader>
                    <h6 className='card-title-secondary'>Details</h6>
                  </CardHeader>
                  <CardBody className='detail-card-body'>
                    <div>
                      <Form
                        onSubmit={onSave}
                        render={(formRenderProps) => (
                          <div className='card-content'>
                            <div className='k-d-flex single-field-row'>
                              <div
                                className={`field-wrapper ${isValidate(validateForm, formData?.TenantName) ? 'field-invalid' : 'field-valid'}`}
                              >
                                <TenantDropdown
                                  textField='TenantName'
                                  label='Tenant'
                                  dataItemKey='TenantId'
                                  className='tenant-dropdown k-align-self-end'
                                  tenant={tenant}
                                  setTenant={setTenant}
                                  defaultItem={{
                                    TenantName: 'Select Tenant ...',
                                    disabled: true,
                                    OrganizationPrefix: '',
                                  }}
                                  editMode={testState?.edit ? 'ReadOnly' : ''}
                                  filterable={true}
                                  tenantFormId='tenant_id'
                                  tenantFormName='TenantName'
                                  setFormData={setFormData}
                                  requiredField={true}
                                />
                                {isValidate(
                                  validateForm,
                                  formData?.TenantName,
                                ) && <Error>This field cannot be empty</Error>}
                              </div>
                            </div>
                            <div className='k-d-flex single-field-row'>
                              <div
                                className={`field-wrapper ${!testState?.edit && 'prefix-field-wrapper'} ${isValidate(validateForm, formData.les_cmd_id) ? 'field-invalid' : 'field-valid'}`}
                              >
                                <Field
                                  key={'les_cmd_id'}
                                  id={'les_cmd_id'}
                                  name={'les_cmd_id'}
                                  label={'Command Id'}
                                  data={formData?.les_cmd_id}
                                  onChange={handleInputChange}
                                  type={'text'}
                                  component={TextInput}
                                  renderTypes={
                                    testState?.edit ? 'ReadOnly' : ''
                                  }
                                  optional={false}
                                  requiredField={true}
                                  prefix={!testState?.edit ? true : false}
                                  prefixLabel={tenant?.OrganizationPrefix}
                                />
                                {isValidate(
                                  validateForm,
                                  formData.les_cmd_id,
                                ) && <Error>This field cannot be empty</Error>}
                              </div>
                              <div className={`field-wrapper`}>
                                <Field
                                  key={'grp_nam'}
                                  id={'grp_nam'}
                                  name={'grp_nam'}
                                  data={formData?.grp_nam}
                                  onChange={handleInputChange}
                                  label={'Group Name'}
                                  type={'text'}
                                  component={TextInput}
                                  renderTypes={
                                    !detailsDataModified ? 'ReadOnly' : ''
                                  }
                                  optional={false}
                                />
                              </div>
                            </div>
                            <div className='k-d-flex single-field-row'>
                              <div
                                className={`field-wrapper field-wrapper-100  ${isValidate(validateForm, formData?.syntax) ? 'field-invalid' : 'field-valid'} field-wrapper-cmd`}
                              >
                                <CommandEditor
                                  renderTypes={
                                    !detailsDataModified ? 'ReadOnly' : ''
                                  }
                                  validateForm={validateForm}
                                  formData={formData}
                                  setFormData={setFormData}
                                  detailsDataModified={detailsDataModified}
                                  isEditor={isEditor}
                                  setIsEditor={setIsEditor}
                                  editorField={{
                                    name: 'syntax',
                                    value: formData?.syntax,
                                    label: 'Syntax',
                                  }}
                                  setEditorField={setEditorField}
                                  smHeight={'100px'}
                                  lgHeight={'70vh'}
                                  requiredField={true}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      />
                    </div>
                  </CardBody>
                </Card>
                {detailsDataModified && (
                  <Card className='k-mt-3'>
                    <CardHeader>
                      <div className='k-d-flex single-field-row button-form-wrapper k-action-buttons !k-px-0'>
                        <>
                          <Button
                            icon={'cancel'}
                            onClick={() => onExit()}
                            type='button'
                          >
                            Cancel
                          </Button>
                          <SplitButton
                            text='save'
                            themeColor={'primary'}
                            onButtonClick={() => onSave({ isExit: false })}
                            onItemClick={handleSaveSplit}
                          >
                            <SplitButtonItem text='Save & Close' />
                          </SplitButton>
                        </>
                      </div>
                    </CardHeader>
                  </Card>
                )}
              </div>
            </>
          )}
        </React.Suspense>
      </div>
    </ErrorBoundary>
  );
};
export default AddCommand;
